import { forEach, groupBy, minBy, sortBy } from 'lodash'

function linearInterpolation(lowerVal, upperVal, span, idx) {
    const yDelta = upperVal - lowerVal
    const yStep = yDelta / span
    return lowerVal + (yStep * idx)
}

export default function Telemetry(state) {
    const getLocation = (interval, playerName, playerLocation) => {
        // Only right datapoint (before first sample)
        if (typeof playerLocation.left === 'undefined') {
            return state[playerLocation.right].playerLocations[playerName]
        }
        // Only left datapoint (after last sample)
        if (typeof playerLocation.right === 'undefined') {
            return state[playerLocation.left].playerLocations[playerName]
        }

        const left = state[playerLocation.left].playerLocations[playerName]
        const right = state[playerLocation.right].playerLocations[playerName]
        const span = playerLocation.right - playerLocation.left

        return {
            x: linearInterpolation(left.x, right.x, span, interval - playerLocation.left),
            y: linearInterpolation(left.y, right.y, span, interval - playerLocation.left),
        }
    }

    const stateAt = msSinceEpoch => {
        const interval = Math.floor(msSinceEpoch / 100)
        const clampedInterval = Math.max(0, Math.min(interval, state.length - 1))

        // Bazı edge-case'lerde (ör. kill tracer'ları için sonradan eklenen
        // aralıklar) state dizisinde boş slotlar oluşabiliyor. Bu durumda,
        // en yakın önceki geçerli state'i kullanarak çökerken hata almamayı
        // sağlıyoruz.
        let s = state[clampedInterval]
        if (!s || !s.players) {
            let fallbackIdx = clampedInterval
            while (fallbackIdx >= 0 && (!state[fallbackIdx] || !state[fallbackIdx].players)) {
                fallbackIdx -= 1
            }
            s = state[fallbackIdx] || state[0]
        }

        // Overwrite player pointer records with interpolated values. This will generate the correct value
        // for this interval and replace the pointer record with it so that a re-request of this interval
        // will not require any computation.
        forEach(s.players, (player, playerName) => {
            if (!Object.hasOwnProperty.call(player, 'location')) {
                if (Object.hasOwnProperty.call(s.playerLocations[playerName], 'left')) {
                    const curLocation = s.playerLocations[playerName]
                    s.playerLocations[playerName] = getLocation(interval, playerName, curLocation)
                }

                s.players[playerName] = {
                    ...s.players[playerName],
                    location: s.playerLocations[playerName],
                }
            }
        })

        // Overwrite bluezone records with interpolated values
        if (s.bluezone && Object.hasOwnProperty.call(s.bluezone, 'left')) {
            if (!s.bluezone.right) {
                s.bluezone = state[s.bluezone.left].bluezone
            } else {
                const left = state[s.bluezone.left].bluezone
                const right = state[s.bluezone.right].bluezone
                if (!left || !right || typeof left.radius !== 'number' || typeof right.radius !== 'number') {
                    s.bluezone = right && typeof right.radius === 'number' ? right : left
                } else {
                    const span = s.bluezone.right - s.bluezone.left
                    s.bluezone = {
                        x: linearInterpolation(left.x, right.x, span, interval - s.bluezone.left),
                        y: linearInterpolation(left.y, right.y, span, interval - s.bluezone.left),
                        radius: linearInterpolation(left.radius, right.radius, span, interval - s.bluezone.left),
                    }
                }
            }
        }

        return s
    }

    const finalRoster = focusedPlayer => {
        if (!state.matchEnd || !state.matchEnd.characters) {
            return []
        }
        const nonBotCharacters = state.matchEnd.characters
            .filter(c => c.character && c.character.accountId
              && !c.character.accountId.startsWith('npc.')
              && !c.character.accountId.startsWith('ai.'))
            .map(c => c.character)

        const rosters = sortBy(groupBy(nonBotCharacters, 'teamId'), r => minBy(r, 'ranking').ranking)
        const focusedRosterIdx = rosters.findIndex(r => r.some(c => c.name === focusedPlayer))
        let focusedRoster
        if (focusedRosterIdx >= 0) {
            [focusedRoster] = rosters.splice(focusedRosterIdx, 1)
        } else {
            focusedRoster = rosters[0] || []
        }
        const sortedRosters = [focusedRoster, ...rosters]
        // Takım içi tekrarları kaldır; aynı takım (aynı oyuncu seti) birden fazla slotta görünmesin
        const withNames = sortedRosters.map(r => [...new Set(r.map(c => c.name))].sort())
        const seen = new Set()
        return withNames.filter(r => {
            const key = r.join(',')
            if (seen.has(key)) return false
            seen.add(key)
            return true
        })
    }

    /** Her aşama (1–8) için güvenli bölge yarıçapı; deneme alanı boyutu için kullanılır. */
    const phaseRadii = {}
    if (Array.isArray(state)) {
        for (let i = 0; i < state.length; i++) {
            const s = state[i]
            if (!s) continue
            const idx = s.circlePhase && s.circlePhase.index
            if (idx >= 1 && idx <= 8 && !phaseRadii[idx] && s.safezone && typeof s.safezone.radius === 'number') {
                phaseRadii[idx] = s.safezone.radius
            }
        }
    }

    return {
        state,
        stateAt,
        finalRoster,
        phaseRadii,
    }
}



// WEBPACK FOOTER //
// ./src/models/Telemetry.js